import { useState, useEffect } from 'react';
import { getSettings, upsertSetting } from '../../utils/api';

// Make a Google Docs/Drive link embeddable in an iframe (Google blocks framing
// of /edit and /view URLs — use /preview). Other URLs pass through unchanged.
function embedUrl(url) {
  if (!url) return url;
  const u = String(url).trim();
  let m = u.match(/^(https:\/\/docs\.google\.com\/(?:document|spreadsheets|presentation)\/d\/[^/]+)/);
  if (m) return m[1] + '/preview';
  m = u.match(/^(https:\/\/drive\.google\.com\/file\/d\/[^/]+)/);
  if (m) return m[1] + '/preview';
  return u;
}

export default function AdminManual({ showToast }) {
  const [savedUrl, setSavedUrl] = useState('');
  const [savedLabel, setSavedLabel] = useState('Servant Leader Manual');
  const [linkInput, setLinkInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Load the currently-saved manual link so it persists across sessions.
  useEffect(() => {
    getSettings()
      .then(s => {
        if (s?.slManualUrl) { setSavedUrl(s.slManualUrl); setLinkInput(s.slManualUrl); }
        if (s?.slManualLabel) setSavedLabel(s.slManualLabel);
      })
      .catch(() => {});
  }, []);

  async function handleSave() {
    const url = linkInput.trim();
    if (!url) { showToast('Paste a link first.'); return; }
    setSaving(true);
    try {
      await upsertSetting('slManualUrl', url);
      await upsertSetting('slManualLabel', 'Servant Leader Manual');
      setSavedUrl(url);
      setSavedLabel('Servant Leader Manual');
      showToast('Servant Leader Manual saved — now visible to all servant leaders.');
    } catch (err) {
      console.error('Failed to save SL manual:', err);
      showToast('Error saving — please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    setSaving(true);
    try {
      await upsertSetting('slManualUrl', '');
      setSavedUrl('');
      setLinkInput('');
      showToast('Servant Leader Manual removed.');
    } catch (err) {
      console.error('Failed to remove SL manual:', err);
      showToast('Error removing — please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Servant Leader Manual</div>
          <div className="page-sub">A link shown to every servant leader on their schedule page</div>
        </div>
      </div>

      <div className="panel" style={{ padding: 28, maxWidth: 660 }}>
        <div style={{ fontFamily: 'var(--font-h)', fontSize: 15, fontWeight: 800, color: 'var(--black)', marginBottom: 6 }}>Manual link</div>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
          Paste a Google Doc, Google Drive, or PDF link. Make sure it's shared <strong>“Anyone with the link → Viewer”</strong> so servant leaders can open it. Once saved, a <strong>Servant Leader Manual</strong> tab appears on every SL's page.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="form-input"
            placeholder="https://drive.google.com/…  or  https://docs.google.com/document/…"
            value={linkInput}
            onChange={e => setLinkInput(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="btn btn-dark" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {savedUrl && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, color: '#1a5c3a' }}>
                ✓ Live · {savedLabel}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <a className="btn btn-xs" href={savedUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>Open</a>
                <button className="btn btn-xs btn-danger" onClick={handleRemove} disabled={saving}>Remove</button>
              </div>
            </div>
            <div className="pdf-viewer-wrap">
              <iframe
                src={embedUrl(savedUrl)}
                className="pdf-viewer-frame"
                style={{ minHeight: 600 }}
                title="Servant Leader Manual"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
